import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  sendEmailVerification,
  reload,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User } from '../types/index';
import { UserService } from './userService';

export class AuthService {
  static async signup(
    email: string,
    password: string,
    name: string,
    role: 'admin' | 'sales' | 'viewer' = 'viewer'
  ): Promise<User> {
    try {
      // Create Firebase auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Auto-approve only if email is from whitelisted domain (redmaxevents.com)
      const isApproved = UserService.isEmailWhitelisted(email);

      // Create user document in Firestore
      const userData: User = {
        uid: firebaseUser.uid,
        email: firebaseUser.email || '',
        name,
        role,
        company: 'Redmax Events',
        createdAt: new Date(),
        isApproved,
        isActive: true, // New users start as active
        status: isApproved ? 'approved' : 'pending',
        emailVerified: false,
      };

      console.log('Attempting to write user to Firestore:', firebaseUser.uid);
      try {
        const firestoreData: any = {
          ...userData,
          createdAt: userData.createdAt.toISOString(),
        };

        // Only set these if user is approved
        if (isApproved) {
          firestoreData.emailVerifiedAt = new Date().toISOString();
          firestoreData.approvedAt = new Date().toISOString();
        }

        await setDoc(doc(db, 'users', firebaseUser.uid), firestoreData);
        console.log('User document created in Firestore successfully');
      } catch (firestoreError: any) {
        console.error('Failed to write user to Firestore:', firestoreError);
        console.warn('User created in Firebase Auth but NOT in Firestore - this may be a Cloudflare/network issue');
        // Don't throw - let signup succeed anyway, user can still authenticate via Firebase Auth
      }

      // Attempt to send verification email (may fail if templates not configured)
      try {
        await sendEmailVerification(firebaseUser, {
          url: `${window.location.origin}/verify-email`,
          handleCodeInApp: true,
        });
      } catch (emailError: any) {
        console.warn('Email verification failed (templates may not be configured):', emailError.message);
        // Don't throw - let signup succeed anyway
      }

      return userData;
    } catch (error: any) {
      throw new Error(`Signup failed: ${error.message}`);
    }
  }

  static async login(email: string, password: string): Promise<FirebaseUser> {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;

      // Reload to get latest verification status
      await reload(firebaseUser);

      return firebaseUser;
    } catch (error: any) {
      throw new Error(`Login failed: ${error.message}`);
    }
  }

  static async loginWithGoogle(): Promise<FirebaseUser> {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const firebaseUser = userCredential.user;

      // Check if user document exists, if not create it
      try {
        const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

        if (!userDoc.exists()) {
          // First time Google login - create user document
          const isApproved = UserService.isEmailWhitelisted(firebaseUser.email || '');

          const userData: any = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
            role: 'viewer',
            company: 'Redmax Events',
            createdAt: new Date().toISOString(),
            isApproved,
            isActive: true, // Set to true initially
            status: isApproved ? 'approved' : 'pending',
            emailVerified: true, // Google auto-verifies
          };

          // Only set these if user is approved
          if (isApproved) {
            userData.emailVerifiedAt = new Date().toISOString();
            userData.approvedAt = new Date().toISOString();
          }

          await setDoc(doc(db, 'users', firebaseUser.uid), userData);
        }
      } catch (dbError: any) {
        console.error('Firestore error during Google login:', dbError);
        // If Firestore is unavailable, just continue - the user is authenticated
        // and the auth state listener will handle loading their data
      }

      return firebaseUser;
    } catch (error: any) {
      throw new Error(`Google login failed: ${error.message}`);
    }
  }

  static async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error: any) {
      throw new Error(`Logout failed: ${error.message}`);
    }
  }

  static async getUserData(uid: string): Promise<User | null> {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const data = userDoc.data();

        // Get current Firebase user to check email verification status
        const firebaseUser = auth.currentUser;
        const emailVerified = firebaseUser?.emailVerified ?? false;

        return {
          ...data,
          createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
          emailVerified,
          emailVerifiedAt: data.emailVerifiedAt ? new Date(data.emailVerifiedAt) : undefined,
        } as User;
      }
      console.warn('User document not found for uid:', uid);
      return null;
    } catch (error: any) {
      console.error('Failed to fetch user data:', error);
      // Return null instead of throwing - let the app continue
      return null;
    }
  }

  static onAuthStateChange(callback: (user: User | null) => void): () => void {
    return onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          console.log('Firebase user authenticated:', firebaseUser.email);

          // CRITICAL: Always fetch from Firestore first before calling callback
          // This ensures we have the actual approval/active status
          try {
            const userData = await this.getUserData(firebaseUser.uid);

            if (userData) {
              console.log('Firestore user data loaded:', {
                email: userData.email,
                isApproved: userData.isApproved,
                status: userData.status,
                isActive: userData.isActive,
                role: userData.role
              });
              // Call callback with ACTUAL user data from Firestore
              callback(userData);
            } else {
              console.warn('User document not found in Firestore for uid:', firebaseUser.uid);
              // User doesn't exist in Firestore - definitely not approved
              callback(null);
            }
          } catch (firestoreError: any) {
            console.error('Failed to load Firestore data, blocking user:', firestoreError.message);
            // On Firestore error, block access for security
            callback(null);
          }
        } else {
          console.log('No Firebase user authenticated');
          callback(null);
        }
      } catch (error: any) {
        console.error('Error in auth state change listener:', error);
        callback(null);
      }
    });
  }
}
